import { css } from '@emotion/react';
import React, { useState } from 'react';
import { AiProfileInfo, OnboardingAiProfileType } from '@/customTypes/chapter';
import StoryGameCharacterList from './StoryGameCharacterList';
import StoryGameCharacterNameInput from './StoryGameCharacterNameInput';
import { AiCharacterImageType, MainCharacter } from '@/customTypes/storygame';
import { SPLoading } from '@/components/commons';
import theme from '@/styles/theme';
import StoryGamePromotion from './StoryGamePromotion';
import SpeechBubble from '@/components/commons/popup/StoryGameSpeechBubble';
import { IStoryGameOnboardingAlert } from '@/components/storyGame/popup/StoryGameOnboardingAlert';
import StoryGameAiImageButton from '@/components/commons/popup/StoryGameAiImageButton';
import StoryGameMyImageDefaultBubble from '@/components/commons/popup/StoryGameMyImageDefaultBubble';

import {
  CheckboxOnBtn,
  StoryGamePromotionImg,
  StoryGamePromotionImg2,
  StoryGamePromotionImg3,
} from 'public/assets';
import useImagePreloader from '@/hooks/useImagePreloader';
import { useUnsavedChangesWarning } from '@/hooks/useUnsavedChangesWarning';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import useTranslation from 'next-translate/useTranslation';

interface StoryGameAiImageSelectPopupProps {
  setAiProfileType: (type: OnboardingAiProfileType) => void;
  onNext: () => void;
  onChangeAiProfile: (newAiProfileInfo: AiProfileInfo) => void;
  onGetAiCharacterSessionId: () => Promise<void>;

  onGetAiCharacterImageList: (chrId?: number) => Promise<void>;
  aiCharacterImageList?: AiCharacterImageType[]; //profileImage는 default캐릭터에만 있음
  onClickStoryGameCharacter: (character: AiProfileInfo) => void;
  selectedCharacter?: MainCharacter;
  onCreateCharacter: () => Promise<void>;
  aiProfileInfo?: AiProfileInfo;
  aiProfileInfoArray?: AiProfileInfo[];
  addOpenedStorygameIds: () => void;
  openedStorygameIds?: string;
  isShowPromotionStorygame: boolean;
  setIsShowPromotionStorygame: (value: boolean) => void;
  bg?: string;
  selectPreviousCharacter: () => void;
  selectNextCharacter: () => void;
  storygameAlertData: IStoryGameOnboardingAlert;
  setStorygameAlertData: (data: IStoryGameOnboardingAlert) => void;
  isNameMissingForSave: () => boolean;
  onClickMyImage: (character: AiCharacterImageType) => void;
}

const StoryGameAiImageSelectPopup = ({
  setAiProfileType,

  onChangeAiProfile,
  aiCharacterImageList,

  onClickStoryGameCharacter,
  selectedCharacter,
  onCreateCharacter,
  aiProfileInfo,
  aiProfileInfoArray,
  isShowPromotionStorygame,
  setIsShowPromotionStorygame,
  bg,
  selectPreviousCharacter,
  selectNextCharacter,
  onClickMyImage,
  isNameMissingForSave,
}: StoryGameAiImageSelectPopupProps) => {
  const [isShowGuideBubble] = useState(isShowPromotionStorygame);
  const [isHovered, setIsHovered] = useState(false);
  const { isPC } = useWindowWidth();
  const { t } = useTranslation();
  const images = [
    StoryGamePromotionImg,
    StoryGamePromotionImg2,
    StoryGamePromotionImg3,
  ];
  const imagesPreloaded = useImagePreloader(images);

  useUnsavedChangesWarning(true);

  return (
    <div css={styles(bg ?? '')}>
      <div className="bg-image" />
      <div className={`bg${isShowPromotionStorygame ? '-promotion' : ''}`} />
      <div className="top-container">
        {/* <div className="top-left">
          <HomeIcon />
        </div> */}
        <div className="top-title">
          {isShowPromotionStorygame
            ? ''
            : isPC
            ? t('storygame_ai_onboarding_screen_description_save_and_done')
            : t('storygame_ai_onboarding_screen_title_character_setup')}
        </div>
        {!isPC && isShowPromotionStorygame ? null : (
          <div
            className={
              isNameMissingForSave() ? 'top-right' : 'top-right-disabled'
            }
            onClick={() => onCreateCharacter()}
          >
            {t('storygame_ai_onboarding_screen_button_save')}
          </div>
        )}
      </div>

      <div
        className="container"
        style={isShowPromotionStorygame && !isPC ? { display: 'none' } : {}}
      >
        <div
          className="side-container"
          style={{ opacity: isShowPromotionStorygame ? 0.7 : 1 }}
        >
          <div className="character-list-setup">
            <StoryGameCharacterList
              characterList={aiProfileInfoArray}
              selectedCharacter={selectedCharacter}
              onClickStoryGameCharacter={onClickStoryGameCharacter}
            />
          </div>
        </div>
        <div className="image-container">
          {!isShowPromotionStorygame && (
            <>
              {aiProfileInfo?.portraitFile?.link ? (
                <img
                  src={aiProfileInfo?.portraitFile?.link}
                  className="ai_character"
                  alt="ai_character"
                />
              ) : (
                <div className="ai_character">
                  <div css={loadingCss('4rem')}>
                    <div className="loading" />

                    <div className="loading-text1">
                      {t(
                        'storygame_ai_onboarding_screen_description_generating',
                      )}
                    </div>
                    <div className="loading-text2">
                      {t(
                        'storygame_ai_onboarding_screen_description_estimated_time',
                        { value: 1 },
                      )}
                    </div>
                    <div className="loading-text3">
                      {t(
                        'storygame_ai_onboarding_screen_description_know_this_warning',
                      )}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
          <div className="bottom-container">
            {!isShowPromotionStorygame && selectedCharacter && aiProfileInfo && (
              <StoryGameCharacterNameInput
                characterImage={
                  aiProfileInfo?.profileFile ??
                  aiProfileInfo?.defaultProfileImageFile?.link
                }
                firstName={aiProfileInfo?.firstName ?? ''}
                lastName={aiProfileInfo?.lastName ?? ''}
                placeholderLastName={selectedCharacter.lastName}
                placeholderFirstName={selectedCharacter.firstName}
                displayName={selectedCharacter.displayName}
                onChangeAiProfile={onChangeAiProfile}
                onProfileEdit={function (): void {
                  setAiProfileType('ProfileCrop');
                }}
                isDefault={
                  aiCharacterImageList?.find((characterImage) => {
                    return (
                      characterImage.profileImage?.fileId ===
                      aiProfileInfo?.defaultProfileImageFile?.fileId
                    );
                  })?.isDefault ?? true
                }
                onPrevious={selectPreviousCharacter}
                onNext={selectNextCharacter}
                isError={aiProfileInfo.isError}
              />
            )}
          </div>
        </div>

        <div
          className="side-container-right"
          style={{ opacity: isShowPromotionStorygame ? 0.7 : 1 }}
        >
          <div className="ai-character-setup">
            <h2 className="title">
              {t('storygame_ai_onboarding_screen_title_my_image')}
            </h2>

            <div
              className="ai-image-section"
              onClick={() => {
                // addOpenedStorygameIds();
                if (aiProfileInfo?.cropImageFile) {
                  setAiProfileType('AiImageFilter');
                  return;
                }
                setAiProfileType('AiImageUpload');
              }}
            >
              <StoryGameAiImageButton />
              {/* <div className="ai-icon">
                <MagicWand />
              </div> */}
              {/* <p className="ai-text">AI 이미지</p>
              <p className="ai-text">생성</p> */}
              {isShowGuideBubble && (
                <div
                  style={{
                    position: 'absolute',
                    left: 'calc(-100% - 10px)',
                  }}
                >
                  <SpeechBubble />
                </div>
              )}
            </div>

            <div className="divider"></div>

            <div className="ai-character-list-container">
              <div className="ai-character-list">
                {aiCharacterImageList?.map((character, idx) => {
                  const isSelected = aiProfileInfo?.requestId === character.id;
                  return (
                    <div
                      style={{ position: 'relative' }}
                      key={character.imageFile?.fileId + '_' + idx}
                    >
                      <div
                        className={`ai-character-section ${
                          isSelected ? 'selected' : ''
                        }`}
                        onClick={() => onClickMyImage(character)}
                      >
                        {character.imageFile?.link ? (
                          <img
                            className="ai-character-image"
                            src={character.imageFile?.link}
                            alt={`Character ${JSON.stringify(
                              character.imageFile?.fileId,
                            )}`}
                          />
                        ) : (
                          <div className="ai-character-image">
                            <SPLoading isFixed size="2rem" />
                          </div>
                        )}
                        {isSelected && (
                          <div className="overlay-bg">
                            <div className="status-info">
                              <CheckboxOnBtn />
                            </div>
                          </div>
                        )}

                        {character.isDefault && (
                          <span
                            className="info-icon"
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                          >
                            ⓘ
                          </span>
                        )}
                      </div>
                      {isHovered && character.isDefault ? (
                        <div
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            width: '90%',
                            transform:
                              idx === 0
                                ? 'translate(-50%, 80%)'
                                : 'translate(-50%, -110%)',
                          }}
                        >
                          <StoryGameMyImageDefaultBubble
                            trianglePosition={idx === 0 ? 'top' : 'bottom'}
                          >
                            {t(
                              'storygame_ai_onboarding_screen_description_preset_image_by_the_author',
                            )}
                          </StoryGameMyImageDefaultBubble>
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {isShowPromotionStorygame && imagesPreloaded && (
        <StoryGamePromotion
          setIsShowPromotionStorygame={setIsShowPromotionStorygame}
        />
      )}

      {/* <div style={{ position: 'absolute', zIndex: 10 }}>
        <StoryGameHtmlDownload htmlContent={testHtml} />
      </div> */}
    </div>
  );
};

const baseButtonStyle = css`
  position: absolute;
  right: 12px;
  display: flex;
  height: 48px;
  min-height: 48px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
`;

const styles = (bg: string) => css`
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .bg {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #00000040;
    backdrop-filter: blur(8px);
  }
  .bg-promotion {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #000000bf;
    backdrop-filter: blur(8px);
  }

  .top-container {
    display: flex;
    width: 100%;
    flex-direction: row;
    align-items: center;

    padding: 12px;
    height: 72px;
    justify-content: center;

    border-bottom: 1px solid rgba(255, 255, 255, 0.5);
    z-index: 1;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.25) 0%,
      rgba(0, 0, 0, 0) 100%
    );

    @media screen and (max-width: ${theme.devices.md}px) {
      border-bottom: 0px;
    }
  }

  .top-container .top-left {
    position: absolute;
    left: 12px;
    display: flex;
    align-items: center;
  }

  .top-container .top-right {
    ${baseButtonStyle}
    color: #fff;
    background: #753bbd;
  }

  .top-container .top-right-disabled {
    ${baseButtonStyle}
    color: var(--whiteA25, rgba(255, 255, 255, 0.25));
    background: var(--whiteA10, rgba(255, 255, 255, 0.1));
    cursor: not-allowed;
  }

  .top-container .top-title {
    color: #ffffff;
    font-size: 18px;
    font-weight: 500;
    position: absolute;
  }

  .container {
    width: 100%;
    flex: 1;

    position: relative;
    display: flex;

    flex-direction: row;
    overflow: hidden;
    max-width: 1920px;
  }
  .image-container {
    height: 100%;

    width: 100%;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: end;
    // border-radius: 0.625rem;
    // border: 1px solid rgba(255, 255, 255, 0.25);

    @media screen and (max-width: 1024px) {
      min-width: 480px;
    }
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }

  .container .ai_character {
    display: flex;
    justify-content: center;
    object-fit: contain;

    height: 85vh;

    @media screen and (max-width: 1024px) {
      min-width: 480px;
      min-height: 480px;
    }
  }

  .bottom-container {
    position: absolute;
    bottom: 10%;
  }

  .side-container {
    display: flex;
    flex-direction: column;

    margin: 20px 0px;
    align-items: center;
    justify-content: center;

    padding: 0px 0px 0px 48px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;
    z-index: 1;
  }
  .character-list-setup {
    position: relative;

    color: white;
    height: 580px;
    display: flex;
    flex-direction: column;
  }

  .side-container-right {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 20px 0px;
    align-items: end;
    justify-content: center;

    padding: 0px 48px 0px 0px;
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* Internet Explorer 10+ */
    flex-direction: column;
  }

  .ai-character-setup {
    position: relative;
    padding: 20px 0px;
    color: white;
    height: 580px;
    display: flex;
    flex-direction: column;

    border-radius: 12px;
    background: rgba(0, 0, 0, 0.5);
  }

  .title {
    text-align: center;
    margin-bottom: 20px;
    color: #fff;

    font-size: 18px;
    font-weight: 500;
  }

  .ai-image-section {
    width: 100px;
    height: 100px;
    border-radius: 8px;
    margin: 0px 20px;

    text-align: center;

    font-size: 14px;
    justify-content: center;
    align-items: center;
    display: flex;
    margin-bottom: 8px;
    cursor: pointer;
  }

  .ai-icon {
    width: 24px;
    height: 24px;
    font-size: 24px;
    margin-bottom: 10px;
  }

  .ai-text {
    margin: 0;
  }
  .ai-character-list-container {
    position: relative;
    width: 100%;
    overflow-y: scroll;
    height: 400px; /* 또는 원하는 높이 */
    margin-bottom: 8px;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
  }
  .ai-character-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px 0;
    height: 400px;
  }

  .ai-character-section {
    width: 100px;
    height: 100px;
    flex-shrink: 0;
    border-radius: 10px;
    border: solid 1px transparent;
    display: flex;
    align-items: center;
    position: relative;
    justify-content: center;
    cursor: pointer;
    transition: border-color 0.3s ease;
    border-color: #ffffff80;
    opacity: 0.75;
    margin: 0px 20px;
  }

  .ai-character-section.selected {
    border-color: #9457eb;
    opacity: 1;

    overflow: hidden;
  }

  .ai-character-image {
    width: 100%;
    height: 100%;
    border-radius: 5px;
    object-fit: contain;
  }
  .overlay-bg {
    width: 100%;
    height: 100%;
    background-color: #9457eb40;
    position: absolute;
    top: 0px;
    left: 0px;
  }

  .status-info {
    position: absolute;
    top: 0px;
    left: 0px;
    background-color: #9457eb;
    border-radius: 5px 0px 5px 0px;
    width: 24px;
    height: 24px;
  }

  .ai-character-status {
    margin: 0;
    font-size: 13px;
    color: #ddd;
  }

  .info-icon {
    font-size: 16px;
    position: absolute;
    top: 6px;
    right: 6px;
    color: #ffffffbf;
  }

  .loading-text1 {
    margin-top: 5px;
    font-size: 18px;
    font-weight: 500;
    line-height: 24px;
    text-align: center;
    color: #ffffff;
  }
  .loading-text2 {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    text-align: center;
    color: #ffffffbf;
  }
  .loading-text3 {
    display: flex;
    padding: var(--spacing-ms, 12px) 16px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.05);
    color: var(--whiteA50, rgba(255, 255, 255, 0.5));
    text-align: center;
    font-size: 12px;
    line-height: 16px; /* 133.333% */
    margin-top: 12px;
  }

  .divider {
    min-height: 1px;
    background: var(--whiteA25, rgba(255, 255, 255, 0.25));
    margin: 8px 16.5px;
  }

  .absolute-element {
    position: absolute;
    top: 10px;
    right: -50px;
    width: 100px;
    height: 50px;
    background-color: rgba(255, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    color: white;
  }

  // @media screen and (max-width: 1024px) {
  //   width: 1024px;
  // }
`;

const loadingCss = (size: string) => css`
  justify-content: center;
  align-items: center;
  display: flex;
  flex-direction: column;
  .loading {
    width: ${size ? size : '8em'};
    height: ${size ? size : '8em'};
    border-radius: 50%;
    border: 5px solid ${theme.colors.primary500};
    border-top: 3px solid transparent;
    animation: spin 1s linear infinite;
    @media screen and (max-width: ${theme.devices.mobile}) {
      width: ${size ? size : '4em'};
      height: ${size ? size : '4em'};
      border-width: 3px;
    }
  }
`;

export default StoryGameAiImageSelectPopup;
